/**
 * Teacher Dashboard Page
 *
 * Overview page for teachers showing summary of classes and students.
 * Director (Elienai) can see all teachers and their classes.
 */

import { Link } from "react-router-dom";
import { Spinner } from "../../components/ui";
import { useTeacherDashboard } from "../../hooks/useTeacher";
import { useAuthStore } from "../../store";

export function TeacherDashboardPage() {
  const { user } = useAuthStore();
  const { data, isLoading, error } = useTeacherDashboard();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-earth-400"></i>
          </div>
          <p className="text-earth-600 font-medium">Error loading dashboard</p>
          <p className="text-earth-700 text-sm mt-1">Please try again later</p>
        </div>
      </div>
    );
  }

  const { summary, teachers } = data;
  const isDirector = data.user.isDirector;

  // Debug: Log dashboard data to check isDirector
  console.log("[TeacherDashboard] Dashboard data:", {
    isDirector,
    user: data.user,
    authUser: user,
    authUserIsStaff: user?.isStaff,
  });

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 text-earth-700 text-sm mb-2">
            <i className="ri-user-star-line"></i>
            <span>Teacher Panel</span>
            {isDirector && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                Director
              </span>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
            Hello, {user?.firstName || "Teacher"}
          </h1>
          <p className="text-earth-700 text-sm mt-1">
            {isDirector
              ? "You have full access to all classes and students."
              : "Manage your classes and track your students."}
          </p>
        </div>

        {/* Quick Stats */}
        <div
          className={`grid grid-cols-2 ${isDirector ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-3 sm:gap-4 mb-6 sm:mb-8`}
        >
          <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-olive-100 rounded-lg flex items-center justify-center">
                <i className="ri-group-line text-olive-600 text-lg"></i>
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-earth-800">
              {summary.totalClasses}
            </p>
            <p className="text-earth-700 text-sm">Classes</p>
          </div>

          <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <i className="ri-user-3-line text-emerald-600 text-lg"></i>
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-earth-800">
              {summary.totalStudents}
            </p>
            <p className="text-earth-700 text-sm">Active students</p>
          </div>

          <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-stack-line text-purple-600 text-lg"></i>
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-earth-800">
              {summary.levels.length}
            </p>
            <p className="text-earth-700 text-sm">Levels</p>
          </div>

          {isDirector && teachers && (
            <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <i className="ri-user-star-line text-amber-600 text-lg"></i>
                </div>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-earth-800">
                {teachers.length}
              </p>
              <p className="text-earth-700 text-sm">Teachers</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${isDirector ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4 mb-6 sm:mb-8`}
        >
          <Link
            to="/teacher/calendar"
            className="flex items-center gap-4 p-4 sm:p-5 bg-white rounded-xl border border-earth-200 hover:border-blue-300 hover:bg-blue-50/30 transition-colors group"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <i className="ri-calendar-2-line text-blue-600 text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-earth-800 group-hover:text-blue-800">
                My Calendar
              </h3>
              <p className="text-sm text-earth-700">
                View schedule and manage class changes
              </p>
            </div>
            <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-blue-600"></i>
          </Link>

          <Link
            to="/teacher/classes"
            className="flex items-center gap-4 p-4 sm:p-5 bg-white rounded-xl border border-earth-200 hover:border-olive-300 hover:bg-olive-50/30 transition-colors group"
          >
            <div className="w-12 h-12 bg-olive-100 rounded-xl flex items-center justify-center group-hover:bg-olive-200 transition-colors">
              <i className="ri-calendar-schedule-line text-olive-600 text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-earth-800 group-hover:text-olive-800">
                Manage Classes
              </h3>
              <p className="text-sm text-earth-700">
                View and edit Google Meet and Whiteboard links
              </p>
            </div>
            <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-olive-600"></i>
          </Link>

          <Link
            to="/teacher/extra-materials"
            className="flex items-center gap-4 p-4 sm:p-5 bg-white rounded-xl border border-earth-200 hover:border-purple-300 hover:bg-purple-50/30 transition-colors group"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <i className="ri-attachment-2 text-purple-600 text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-earth-800 group-hover:text-purple-800">
                Extra Materials
              </h3>
              <p className="text-sm text-earth-700">
                Share homework / links with your classes
              </p>
            </div>
            <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-purple-600"></i>
          </Link>

          {isDirector && (
            <>
              <Link
                to="/teacher/students"
                className="flex items-center gap-4 p-4 sm:p-5 bg-white rounded-xl border border-earth-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors group"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                  <i className="ri-user-search-line text-emerald-600 text-xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-earth-800 group-hover:text-emerald-800">
                    View Students
                  </h3>
                  <p className="text-sm text-earth-700">
                    Personal data, classes and payment status
                  </p>
                </div>
                <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-emerald-600"></i>
              </Link>

              <Link
                to="/admin"
                className="flex items-center gap-4 p-4 sm:p-5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 hover:border-amber-300 hover:from-amber-100 hover:to-orange-100 transition-colors group"
              >
                <div className="w-12 h-12 bg-amber-200 rounded-xl flex items-center justify-center group-hover:bg-amber-300 transition-colors">
                  <i className="ri-settings-4-line text-amber-700 text-xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 group-hover:text-amber-900">
                    Admin Panel
                  </h3>
                  <p className="text-sm text-amber-600">
                    Create and manage teachers, students and classes
                  </p>
                </div>
                <i className="ri-arrow-right-s-line text-amber-500 text-xl group-hover:text-amber-700"></i>
              </Link>
            </>
          )}
        </div>

        {/* Levels Summary */}
        <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5 mb-6 sm:mb-8">
          <h2 className="text-lg font-semibold text-earth-800 mb-4">
            Summary by Level
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {summary.levels.map((level) => (
              <Link
                key={level.code}
                to={`/teacher/classes?level=${level.code}`}
                className="p-4 sm:p-5 bg-earth-50 hover:bg-olive-50 rounded-lg transition-colors group"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xl sm:text-2xl font-bold text-earth-800 group-hover:text-olive-800">
                    {level.code}
                  </span>
                  <i className="ri-arrow-right-s-line text-earth-400 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </div>
                <p className="text-sm sm:text-base text-earth-700 mb-1 truncate">
                  {level.name}
                </p>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm text-earth-600">
                  <span>{level.classCount} classes</span>
                  <span className="text-earth-300">•</span>
                  <span>{level.studentCount} students</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Teachers List (Director Only) */}
        {isDirector && teachers && teachers.length > 0 && (
          <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-earth-800 mb-4">
              Teachers
            </h2>
            <div className="space-y-3">
              {teachers.map((teacher) => (
                <Link
                  key={teacher.id}
                  to={`/teacher/classes?teacher=${teacher.id}`}
                  className="flex items-center gap-4 p-3 sm:p-4 bg-earth-50 hover:bg-amber-50 rounded-lg transition-colors group"
                >
                  <div className="w-10 h-10 bg-earth-200 rounded-full flex items-center justify-center text-earth-600 font-semibold group-hover:bg-amber-200 group-hover:text-amber-700">
                    {teacher.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-earth-800 truncate group-hover:text-amber-800">
                      {teacher.name}
                    </p>
                    <p className="text-sm text-earth-700 truncate">
                      {teacher.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-earth-600">
                    <span className="hidden sm:inline">
                      {teacher.classCount} classes
                    </span>
                    <span className="hidden sm:inline text-earth-300">•</span>
                    <span>{teacher.studentCount} students</span>
                    <i className="ri-arrow-right-s-line text-earth-400 group-hover:text-amber-600"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TeacherDashboardPage;
